import { useState } from "react";

import {
  searchGasAddressesByPostcode,
  getGasMeterDetailsByAddressId,
} from "../services/gas.service";
import { GasAddressItem, GasSwitchDataItem } from "../types/services.types";
// Import the API functions to search for addresses and get meter details.

export const useGaslinkAddressSearch = () => {
  const [postcodeInput, setPostcodeInput] = useState<string>("");
  const [houseNameInput, setHouseNameInput] = useState<string>("");
  const [houseNumberInput, setHouseNumberInput] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [addressResults, setAddressResults] = useState<GasAddressItem[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<string>("");
  const [meterDetails, setMeterDetails] = useState<GasSwitchDataItem | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Define an async function for address searching. Since API requests may take time, async/await is used for better API handling.
  const handleSearch = async () => {
    // Clear previous results at the beginning of any new search.
    setAddressResults([]);
    setSelectedAddress("");
    setMeterDetails(null);

    // Trim any white-spaces from inputs
    const cleanPostcode = postcodeInput.trim();
    const cleanHouseName = houseNameInput.trim();
    const cleanHouseNumber = houseNumberInput.trim();

    // Validation for empty postcode field submissions.
    if (!cleanPostcode) {
      setError("Please enter a postcode.");
      return;
    }

    // Ensure that only ONE optional field is populated: house name OR house number.
    if (cleanHouseName && cleanHouseNumber) {
      setError("Please fill in only one optional field: house name OR house number.");
      return;
    }

    // Clear any previous error messages before making a new API call.
    setError(null);
    setIsLoading(true);

    try {
      // Call the searchGasAddressesByPostcode function with the user's inputs.
      const result = await searchGasAddressesByPostcode({
        postcode: cleanPostcode,
        houseName: cleanHouseName || undefined,
        houseNumber: cleanHouseNumber || undefined,
      });

      if (result.success && result.addresses) {
        setAddressResults(result.addresses);

        // Display an error if no addresses are found.
        if (result.addresses.length === 0) {
          setError("No addresses found for this postcode.");
        }
      } else {
        setError(result.message ?? "An error occurred while searching.");
      }
    } catch (err) {
      setError("An error occurred while searching. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Define an async function to handle address selection and fetch meter details for the selected address.
  const handleAddressSelection = async (selectedAddressId: string) => {
    setSelectedAddress(selectedAddressId);
    setIsLoading(true);
    setError(null);

    // Find the selected address object in the addressResults array by matching the address_id.
    const address = addressResults.find((addr) => addr.address_id === selectedAddressId);

    // Display an error message if the selected address is not found.
    if (!address) {
      setError("No meter details were found for the selected address.");
      setMeterDetails(null);
      setIsLoading(false);
      return;
    }

    try {
      // Call getGasMeterDetailsByAddressId() to fetch meter details and update the state.
      const details = await getGasMeterDetailsByAddressId(address.address_id);
      setMeterDetails(details);
    } catch (err) {
      // Display a message and reset meter details if the API call fails.
      setError("An error occurred while fetching meter details.");
      setMeterDetails(null);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    postcodeInput,
    setPostcodeInput,
    houseNameInput,
    setHouseNameInput,
    houseNumberInput,
    setHouseNumberInput,
    error,
    setError,
    addressResults,
    setAddressResults,
    selectedAddress,
    setSelectedAddress,
    meterDetails,
    setMeterDetails,
    isLoading,
    setIsLoading,
    handleSearch,
    handleAddressSelection,
  };
};
